import ErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import MapPicker from "@/components/Register/mapPicker.component";
import {
  RegistrationUserCompany,
  RegistrationUserCompanyBranch,
  ValidationErrors,
} from "@/components/Register/registrationForm.component";
import { EMPTYADDRESS, INDUSTRY_TYPES } from "@/constants/util.constants";
import { Alert, AlertDescription } from "@/shadcn/components/ui/alert";
import { Badge } from "@/shadcn/components/ui/badge";
import { Button } from "@/shadcn/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card";
import { Input } from "@/shadcn/components/ui/input";
import { Label } from "@/shadcn/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcn/components/ui/tabs";
import { Building, MapPin, Plus, Trash2 } from "lucide-react";
import type React from "react";

//   const [companyType, setCompanyType] = useState<"private" | "business">(
//     "business"
//   );
//   const [companies, setCompanies] = useState<Company[]>([]);
//   const [activeCompanyId, setActiveCompanyId] = useState<string>("");
//   const [activeBranchId, setActiveBranchId] = useState<string>("");

type UserCompaniesProps = {
  companyType: "private" | "business";
  setCompanyType: React.Dispatch<React.SetStateAction<"private" | "business">>;
  companies: RegistrationUserCompany[];
  setCompanies: React.Dispatch<React.SetStateAction<RegistrationUserCompany[]>>;
  activeCompanyId: string;
  setActiveCompanyId: React.Dispatch<React.SetStateAction<string>>;
  activeBranchId: string;
  setActiveBranchId: React.Dispatch<React.SetStateAction<string>>;
  errors: ValidationErrors;
};

const UserCompanies: React.FC<UserCompaniesProps> = ({
  companyType,
  setCompanyType,
  companies,
  setCompanies,
  activeCompanyId,
  setActiveCompanyId,
  activeBranchId,
  setActiveBranchId,
  errors,
}) => {
  const addCompany = () => {
    const newCompany: RegistrationUserCompany = {
      id: `company-${Date.now()}`,
      name: "",
      registrationNo: "",
      vatNo: "",
      industryType: "Miscellaneous / Other",
      contactNumber: "",
      branches: [
        {
          id: `branch-${Date.now()}`,
          name: "Main Branch",
          address: { ...EMPTYADDRESS },
        },
      ],
    };
    setCompanies([...companies, newCompany]);
    setActiveCompanyId(newCompany.id);
    setActiveBranchId(newCompany.branches[0].id);
  };

  const updateCompany = (
    companyId: string,
    updates: Partial<RegistrationUserCompany>
  ) => {
    setCompanies(
      companies.map((company) =>
        company.id === companyId ? { ...company, ...updates } : company
      )
    );
  };

  const deleteCompany = (companyId: string) => {
    const updatedCompanies = companies.filter(
      (company) => company.id !== companyId
    );
    setCompanies(updatedCompanies);

    if (activeCompanyId === companyId && updatedCompanies.length > 0) {
      setActiveCompanyId(updatedCompanies[0].id);
      setActiveBranchId(updatedCompanies[0].branches[0]?.id || "");
    }
  };

  const addBranch = (companyId: string) => {
    const newBranch: RegistrationUserCompanyBranch = {
      id: `branch-${Date.now()}`,
      name: "",
      address: { ...EMPTYADDRESS },
    };

    setCompanies(
      companies.map((company) =>
        company.id === companyId
          ? { ...company, branches: [...company.branches, newBranch] }
          : company
      )
    );
    setActiveBranchId(newBranch.id);
  };

  const updateBranch = (
    companyId: string,
    branchId: string,
    updates: Partial<RegistrationUserCompanyBranch>
  ) => {
    setCompanies(
      companies.map((company) =>
        company.id === companyId
          ? {
              ...company,
              branches: company.branches.map((branch) =>
                branch.id === branchId ? { ...branch, ...updates } : branch
              ),
            }
          : company
      )
    );
  };

  const deleteBranch = (companyId: string, branchId: string) => {
    setCompanies(
      companies.map((company) =>
        company.id === companyId
          ? {
              ...company,
              branches: company.branches.filter(
                (branch) => branch.id !== branchId
              ),
            }
          : company
      )
    );
  };

  const activeCompany = companies.find(
    (company) => company.id === activeCompanyId
  );

  const activeBranch = activeCompany?.branches.find(
    (branch) => branch.id === activeBranchId
  );

  return (
    <>
      {/* Company Type Selection */}
      <div className="space-y-2">
        <Label>
          Company Type <span className="text-red-500">*</span>
        </Label>
        <Select
          value={companyType}
          onValueChange={(value: "private" | "business") => {
            setCompanyType(value);
            if (value === "private") {
              setCompanies([
                {
                  id: "private-1",
                  name: "Private",
                  industryType: "Individual",
                  registrationNo: undefined,
                  vatNo: undefined,
                  contactNumber: "",
                  branches: [
                    {
                      id: "branch-1",
                      name: "Main Address",
                      address: { ...EMPTYADDRESS },
                    },
                  ],
                },
              ]);
              setActiveCompanyId("private-1");
              setActiveBranchId("branch-1");
            } else {
              setCompanies([]);
              setActiveCompanyId("");
              setActiveBranchId("");
            }
          }}
        >
          <SelectTrigger className="w-full max-w-full truncate">
            <SelectValue placeholder="Select company type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="private">Private Individual</SelectItem>
            <SelectItem value="business">Business</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Companies and Branches Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Companies & Branches</h3>
          {/* Moved Add Company button below the Tabs section for better UX */}
        </div>

        {errors.companies && (
          <Alert variant="destructive">
            <AlertDescription className="!text-red-500 opacity-95">
              {errors.companies}
            </AlertDescription>
          </Alert>
        )}

        {companies.length > 0 && (
          <Tabs value={activeCompanyId} onValueChange={setActiveCompanyId}>
            <TabsList className="flex h-auto min-h-fit w-full flex-wrap justify-start gap-2">
              {companies.map((company, index) => (
                <TabsTrigger
                  key={company.id}
                  value={company.id}
                  className="flex h-auto min-w-fit flex-shrink-0 items-center gap-2"
                >
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    {company.name || `Company ${index + 1}`}
                    {companies.length > 1 && companyType === "business" && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="ml-2 h-4 w-4 flex-shrink-0 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCompany(company.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>

            {companies.map((company) => (
              <TabsContent
                key={company.id}
                value={company.id}
                className="space-y-4"
              >
                {/* Company Name */}
                {companyType !== "private" && (
                  <div className="space-y-2">
                    <Label htmlFor={`company-name-${company.id}`}>
                      Company Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`company-name-${company.id}`}
                      value={company.name}
                      onChange={(e) =>
                        updateCompany(company.id, {
                          name: e.target.value,
                        })
                      }
                      placeholder="Enter company name"
                      // disabled={companyType === "private"}
                    />
                  </div>
                )}
                {/* Company Registration Number */}
                {companyType !== "private" && (
                  <div className="space-y-2">
                    <Label htmlFor={`company-regno-${company.id}`}>
                      Registration Number
                    </Label>
                    <Input
                      id={`company-regno-${company.id}`}
                      value={company.registrationNo || ""}
                      onChange={(e) =>
                        updateCompany(company.id, {
                          registrationNo: e.target.value,
                        })
                      }
                      placeholder="Enter registration number"
                      // disabled={companyType === "private"}
                    />
                  </div>
                )}
                {/* Company VAT Number */}
                {companyType !== "private" && (
                  <div className="space-y-2">
                    <Label htmlFor={`company-vatno-${company.id}`}>
                      VAT Number
                    </Label>
                    <Input
                      id={`company-vatno-${company.id}`}
                      value={company.vatNo || ""}
                      onChange={(e) =>
                        updateCompany(company.id, {
                          vatNo: e.target.value,
                        })
                      }
                      placeholder="Enter VAT number"
                      // disabled={companyType === "private"}
                    />
                  </div>
                )}
                {/* Company Industry Type */}
                {companyType !== "private" && (
                  <div className="space-y-2">
                    <Label htmlFor={`company-industry-${company.id}`}>
                      Industry Type <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      // id={`company-industry-${company.id}`}
                      value={company.industryType}
                      onValueChange={(value) =>
                        updateCompany(company.id, {
                          industryType: value,
                        })
                      }
                    >
                      <SelectTrigger className="w-full max-w-full truncate">
                        <SelectValue placeholder="Select industry type" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRY_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {/* Company Contact Number */}
                {companyType !== "private" && (
                  <div className="space-y-2">
                    <Label htmlFor={`company-contact-${company.id}`}>
                      Company Contact Number
                    </Label>
                    <Input
                      id={`company-contact-${company.id}`}
                      value={company.contactNumber}
                      onChange={(e) =>
                        updateCompany(company.id, {
                          contactNumber: e.target.value,
                        })
                      }
                      placeholder="Enter company contact number"
                      // disabled={companyType === "private"}
                    />
                  </div>
                )}

                {/* Branches */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Branches</h4>
                    {/* Moved Add Branch button below the branch list for clarity */}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {company.branches.map((branch) => (
                      <Badge
                        key={branch.id}
                        variant={
                          branch.id === activeBranchId ? "default" : "secondary"
                        }
                        className="flex cursor-pointer items-center gap-2"
                        onClick={() => setActiveBranchId(branch.id)}
                      >
                        <MapPin className="h-3 w-3" />
                        {branch.name || "Unnamed Branch"}
                        {company.branches.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteBranch(company.id, branch.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </Badge>
                    ))}
                  </div>

                  {/* Active Branch Form */}
                  {activeBranch && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">
                          Branch Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor={`branch-name-${activeBranch.id}`}>
                            Branch Name
                          </Label>
                          <Input
                            id={`branch-name-${activeBranch.id}`}
                            value={activeBranch.name}
                            onChange={(e) =>
                              updateBranch(company.id, activeBranch.id, {
                                name: e.target.value,
                              })
                            }
                            placeholder="Enter branch name"
                          />
                        </div>
                        <div className="space-y-2">
                          <ErrorBoundary>
                            <MapPicker
                              // Move all address fields into MapPicker so it manages address entry and autocomplete
                              address={activeBranch.address}
                              activeBranchID={activeBranch.id}
                              onAddressChange={(newAddress) => {
                                updateBranch(company.id, activeBranch.id, {
                                  address: {
                                    ...activeBranch.address,
                                    ...newAddress,
                                  },
                                });
                              }}
                            />
                          </ErrorBoundary>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Add Additional Branch button moved here for better flow */}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={() => addBranch(company.id)}
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Additional Branch
                    </Button>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Add Additional Company button moved here for better flow */}
        {companyType === "business" && (
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={addCompany}
              variant="outline"
              size="sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Additional Company
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default UserCompanies;
